import { useCallback } from 'react'
import { adminRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { Branch } from '@user/store/branches/typings'
import { ISalesTeam } from '@user/store/salesTeam/typings'
import { ITenantUser } from '@user/store/users/typings'

export function useBranchAndSalesTeam(
  allBranches: Branch[],
  allSalesTeams: ISalesTeam[],
  allUsers: ITenantUser[],
) {
  const assignBranchAndDefaultSalesTeam = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { branch, branchId, salesTeamId, salesTeamName } = newRecord.row

      if (branch?.value) {
        branchId.value = allBranches.find((x) => x.name === branch.value)?.id

        if (!salesTeamName?.value) {
          const salesTeam = allSalesTeams.find(
            (x) => x.branchId === branchId.value && x.branchDefault,
          )

          if (salesTeam) {
            salesTeamName.value = salesTeam?.name
            salesTeamId.value = salesTeam?.id
          }
        }
      }

      return newRecord
    },
    [allBranches, allSalesTeams],
  )

  const assignSalesTeamAndDefaultBranch = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { salesTeamId, salesTeamName, branch, branchId } = newRecord.row

      if (salesTeamName?.value) {
        const salesTeam = allSalesTeams.find(
          (x) => x.name === salesTeamName.value,
        )

        if (salesTeam) {
          salesTeamId.value = salesTeam?.id

          if (!branch?.value) {
            branch.value = salesTeam?.branchName
            branchId.value = salesTeam?.branchId
          }
        } else {
          salesTeamId.value = ''
        }
      }

      return newRecord
    },
    [allSalesTeams],
  )

  const assignSaleTeamsForBranch = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { salesTeamName, branchId } = newRecord.row

      salesTeamName.selectOptions = branchId?.value
        ? allSalesTeams
            .filter(({ branchId: id }) => id === branchId.value)
            .map(({ name }) => ({ label: name, value: name }))
        : []

      return newRecord
    },
    [allSalesTeams],
  )

  const assignUsersForSalesTeam = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { assigneeId, salesTeamId } = newRecord.row

      if (!assigneeId) return newRecord

      const userOptions = salesTeamId?.value
        ? allUsers
            .filter(
              (x) =>
                x.isSuperAdmin ||
                x.roleName === adminRoleName ||
                x.salesTeams.find((s) => s.id === salesTeamId.value),
            )
            .map((x) => ({
              label: `${x.fullName} - ${x.emailAddress}`,
              value: x.id,
            }))
        : []

      if (userOptions.length) {
        userOptions.unshift({ label: 'Unassigned', value: '' })
      }

      assigneeId.selectOptions = userOptions

      return newRecord
    },
    [allUsers, allSalesTeams, adminRoleName],
  )

  return {
    assignBranchAndDefaultSalesTeam,
    assignSalesTeamAndDefaultBranch,
    assignSaleTeamsForBranch,
    assignUsersForSalesTeam,
  }
}
